import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Collapse,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import * as api from '../../api/api';

function DepartmentsTab() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Department editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDepartment, setNewDepartment] = useState({ name: '' });

  // Cost center management
  const [expandedDeptId, setExpandedDeptId] = useState(null);
  const [ccDialogOpen, setCcDialogOpen] = useState(false);
  const [editingCc, setEditingCc] = useState(null); // null = new, obj = editing
  const [ccForm, setCcForm] = useState({ name: '' });
  const [activeDeptForCc, setActiveDeptForCc] = useState(null);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.getDepartments();
      let depts = [];
      if (Array.isArray(response)) {
        depts = response;
      } else if (response && Array.isArray(response.departments)) {
        depts = response.departments;
      }
      setDepartments(depts);
      setError('');
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Department CRUD
  const handleEdit = (dept) => {
    setEditingId(dept.id);
    setEditForm({ name: dept.name });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      setError('Department name is required');
      return;
    }
    try {
      await api.updateDepartment(editingId, editForm);
      setEditingId(null);
      setEditForm({ name: '' });
      await fetchDepartments();
      setError('');
      window.dispatchEvent(new CustomEvent('departmentUpdated', {
        detail: { departmentId: editingId, departmentData: editForm }
      }));
    } catch (err) {
      console.error('Error updating department:', err);
      setError('Failed to update department');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await api.deleteDepartment(id);
        if (expandedDeptId === id) setExpandedDeptId(null);
        await fetchDepartments();
        setError('');
        window.dispatchEvent(new CustomEvent('departmentDeleted', {
          detail: { departmentId: id }
        }));
      } catch (err) {
        console.error('Error deleting department:', err);
        setError('Failed to delete department');
      }
    }
  };

  const handleAddNew = async () => {
    if (!newDepartment.name.trim()) {
      setError('Department name is required');
      return;
    }
    try {
      const response = await api.createDepartment(newDepartment);
      setDialogOpen(false);
      setNewDepartment({ name: '' });
      await fetchDepartments();
      setError('');
      window.dispatchEvent(new CustomEvent('departmentCreated', {
        detail: { departmentData: response }
      }));
    } catch (err) {
      console.error('Error creating department:', err);
      setError('Failed to create department');
    }
  };

  // Cost Center CRUD
  const openAddCc = (dept) => {
    setActiveDeptForCc(dept);
    setEditingCc(null);
    setCcForm({ name: '' });
    setCcDialogOpen(true);
  };

  const openEditCc = (dept, cc) => {
    setActiveDeptForCc(dept);
    setEditingCc(cc);
    setCcForm({ name: cc.name });
    setCcDialogOpen(true);
  };

  const handleSaveCc = async () => {
    if (!ccForm.name.trim()) {
      setError('Cost center name is required');
      return;
    }
    try {
      if (editingCc) {
        await api.updateCostCenter(activeDeptForCc.id, editingCc.id, { id: editingCc.id, name: ccForm.name });
      } else {
        await api.createCostCenter(activeDeptForCc.id, { id: '', name: ccForm.name });
      }
      setCcDialogOpen(false);
      setCcForm({ name: '' });
      setEditingCc(null);
      await fetchDepartments();
      setError('');
    } catch (err) {
      console.error('Error saving cost center:', err);
      setError('Failed to save cost center');
    }
  };

  const handleDeleteCc = async (dept, cc) => {
    if (window.confirm(`Delete cost center "${cc.name}"?`)) {
      try {
        await api.deleteCostCenter(dept.id, cc.id);
        await fetchDepartments();
        setError('');
      } catch (err) {
        console.error('Error deleting cost center:', err);
        setError('Failed to delete cost center');
      }
    }
  };

  const filteredDepartments = departments
    .filter(dept => dept.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Departments Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: '#90caf9', '&:hover': { bgcolor: '#5d8fc9' } }}
        >
          Add Department
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search Departments"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          size="small"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Name</TableCell>
              <TableCell>Cost Centers</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredDepartments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {searchTerm ? 'No departments match your search' : 'No departments found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredDepartments.map((dept) => (
                <React.Fragment key={dept.id}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedDeptId(expandedDeptId === dept.id ? null : dept.id)}
                      >
                        {expandedDeptId === dept.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {editingId === dept.id ? (
                        <TextField
                          value={editForm.name}
                          onChange={(e) => setEditForm({ name: e.target.value })}
                          size="small"
                          fullWidth
                        />
                      ) : (
                        dept.name
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {(dept.cost_centers || []).length} cost center{(dept.cost_centers || []).length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {editingId === dept.id ? (
                        <Box>
                          <IconButton onClick={handleSaveEdit} color="primary" size="small">
                            <SaveIcon />
                          </IconButton>
                          <IconButton onClick={handleCancelEdit} color="secondary" size="small">
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box>
                          <IconButton onClick={() => handleEdit(dept)} style={{ color: '#90caf9' }} size="small">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(dept.id)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Cost centers sub-row */}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 0, borderBottom: expandedDeptId === dept.id ? undefined : 'none' }}>
                      <Collapse in={expandedDeptId === dept.id} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 4, py: 2, bgcolor: 'action.hover' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Cost Centers
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => openAddCc(dept)}
                            >
                              Add Cost Center
                            </Button>
                          </Box>
                          {(dept.cost_centers || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No cost centers defined
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Name</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(dept.cost_centers || []).map((cc) => (
                                  <TableRow key={cc.id}>
                                    <TableCell>
                                      <Chip label={cc.name} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton size="small" onClick={() => openEditCc(dept, cc)} style={{ color: '#90caf9' }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" color="error" onClick={() => handleDeleteCc(dept, cc)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Department dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Department</DialogTitle>
        <DialogContent>
          <TextField
            label="Department Name"
            value={newDepartment.name}
            onChange={(e) => setNewDepartment({ name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNew} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Cost Center dialog */}
      <Dialog open={ccDialogOpen} onClose={() => setCcDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCc ? 'Edit Cost Center' : 'Add Cost Center'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Department: {activeDeptForCc?.name}
          </Typography>
          <TextField
            label="Cost Center Name"
            value={ccForm.name}
            onChange={(e) => setCcForm({ name: e.target.value })}
            fullWidth
            margin="normal"
            required
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCc(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCcDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCc} variant="contained">{editingCc ? 'Save' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DepartmentsTab;
